import { createSlice } from "@reduxjs/toolkit";

// Tenant type based on API response
export interface Tenant {
  tenant_id: string;
  business_name: string;
  business_type: string;
  business_type_display: string;
  status: string;
  status_display: string;
  is_active: boolean;
  domain_name: string;
  supplier_id: string | null;
  created_at: string;
  [key: string]: any; // For any additional fields
}

export interface TenantsState {
  tenants: Tenant[];
  tenantsLoading: boolean;
  selectedTenant: Tenant | null;

  // Backend pagination info
  tenantsPagination: {
    count: number;
    next: string | null;
    previous: string | null;
  };

  //create or edit dialog state
  tenantsCreateEditDialogOpen: {
    open: boolean;
    openFor: "create" | "edit" | "view";
    editItemData?: Partial<Tenant>;
  };

  tenantsCreateEditLoading: boolean;
  tenantsCreateEditError: string | null;
  tenantsCreateEditFieldErrors: Record<string, string[]> | null;
  tenantsDeleteLoading?: {
    loading: boolean;
    deleteItemId?: string;
  };

  isTenantDialogOpen: {
    open: boolean;
    deleteItemId: string | undefined;
  };

  // Credentials modal state
  credentialsModal: {
    open: boolean;
    credentials:
      | {
          staff_code?: string;
          pin: string;
          account_id?: string;
          note?: string;
          // For backwards compatibility
          username?: string;
          password?: string;
        }
      | undefined;
    message?: string;
  };
}

const initialState: TenantsState = {
  // Tenants from API
  tenants: [],
  tenantsLoading: false,
  selectedTenant: null,

  // Backend pagination info
  tenantsPagination: {
    count: 0,
    next: null,
    previous: null,
  },

  //create or edit dialog state
  tenantsCreateEditDialogOpen: {
    open: false,
    openFor: "create",
    editItemData: undefined,
  },

  tenantsCreateEditLoading: false,
  tenantsCreateEditError: null,
  tenantsCreateEditFieldErrors: null,
  tenantsDeleteLoading: {
    loading: false,
    deleteItemId: undefined,
  },

  isTenantDialogOpen: {
    open: false,
    deleteItemId: undefined,
  },

  // Credentials modal state
  credentialsModal: {
    open: false,
    credentials: undefined,
    message: undefined,
  },
};

export const tenantsSlice = createSlice({
  name: "tenants",
  initialState,
  reducers: {
    setTenants: (state, action) => {
      state.tenants = action.payload;
    },
    setTenantsLoading: (state, action) => {
      state.tenantsLoading = action.payload;
    },
    setTenantsPagination: (state, action) => {
      state.tenantsPagination = action.payload;
    },

    //create or edit dialog state
    setTenantsCreateEditDialogOpen: (state, action) => {
      state.tenantsCreateEditDialogOpen = action.payload;
    },

    clearTenantsCreateEditDialogOpen: (state) => {
      state.tenantsCreateEditDialogOpen = {
        open: false,
        openFor: "create",
        editItemData: undefined,
      };
    },

    setTenantsCreateEditLoading: (state, action) => {
      state.tenantsCreateEditLoading = action.payload;
    },

    setTenantsCreateEditError: (state, action) => {
      state.tenantsCreateEditError = action.payload;
    },

    setTenantsCreateEditFieldErrors: (state, action) => {
      state.tenantsCreateEditFieldErrors = action.payload;
    },

    setTenantsDeleteLoading: (state, action) => {
      state.tenantsDeleteLoading = action.payload;
    },

    setIsTenantDialogOpen: (state, action) => {
      state.isTenantDialogOpen = action.payload;
    },

    setSelectedTenant: (state, action) => {
      state.selectedTenant = action.payload;
    },

    updateTenant: (state, action) => {
      const index = state.tenants.findIndex(
        (tenant) => tenant.tenant_id === action.payload.tenant_id,
      );
      if (index !== -1) {
        state.tenants[index] = {
          ...state.tenants[index],
          ...action.payload.updates,
        };
        state.tenants = [...state.tenants];
      }
    },

    removeTenant: (state, action) => {
      const index = state.tenants.findIndex(
        (tenant) => tenant.tenant_id === action.payload,
      );
      if (index !== -1) {
        state.tenants.splice(index, 1);
        state.tenants = [...state.tenants];
      }
    },

    // Credentials modal actions
    setCredentialsModal: (state, action) => {
      state.credentialsModal = action.payload;
    },

    closeCredentialsModal: (state) => {
      state.credentialsModal = {
        open: false,
        credentials: undefined,
        message: undefined,
      };
    },
  },
});

export const {
  setTenants,
  setTenantsLoading,
  setTenantsPagination,
  setTenantsCreateEditDialogOpen,
  clearTenantsCreateEditDialogOpen,
  setTenantsCreateEditLoading,
  setTenantsCreateEditError,
  setTenantsCreateEditFieldErrors,
  setTenantsDeleteLoading,
  setIsTenantDialogOpen,
  setSelectedTenant,
  updateTenant,
  removeTenant,
  setCredentialsModal,
  closeCredentialsModal,
} = tenantsSlice.actions;

export default tenantsSlice.reducer;
